import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  X,
  FileSearch,
  Calendar,
  Users,
  Search,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';

const InvestigacionNueva = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    titulo: '',
    clientes: [],
    clienteInput: '',
    fecha_desde: '',
    fecha_hasta: '',
    tipo_analisis: 'individual'
  });
  const [errors, setErrors] = useState({});

  const handleAddCliente = () => {
    const cliente = formData.clienteInput.trim();
    if (!cliente) {
      toast.error('Ingrese un CUIT o CVU/CBU');
      return;
    }
    
    // Validar formato
    const isValidCuit = /^\d{11}$/.test(cliente);
    const isValidCvu = /^\d{22}$/.test(cliente);
    
    if (!isValidCuit && !isValidCvu) {
      toast.error('Formato inválido. Use CUIT (11 dígitos) o CVU/CBU (22 dígitos)');
      return;
    }
    
    if (formData.clientes.includes(cliente)) {
      toast.error('Este cliente ya está agregado');
      return;
    }
    
    setFormData({
      ...formData,
      clientes: [...formData.clientes, cliente],
      clienteInput: ''
    });
  };

  const handleRemoveCliente = (cliente) => {
    setFormData({
      ...formData,
      clientes: formData.clientes.filter(c => c !== cliente)
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCliente();
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (formData.clientes.length === 0) {
      newErrors.clientes = 'Debe agregar al menos un cliente';
    }
    
    if (!formData.fecha_desde) {
      newErrors.fecha_desde = 'Fecha de inicio requerida';
    }
    
    if (!formData.fecha_hasta) {
      newErrors.fecha_hasta = 'Fecha de fin requerida';
    }
    
    if (formData.fecha_desde && formData.fecha_hasta && formData.fecha_desde > formData.fecha_hasta) {
      newErrors.fecha_hasta = 'La fecha de fin debe ser posterior a la de inicio';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor corrija los errores del formulario');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        clientes: formData.clientes,
        fecha_desde: formData.fecha_desde,
        fecha_hasta: formData.fecha_hasta,
        tipo_analisis: formData.tipo_analisis,
        titulo: formData.titulo || undefined
      };
      
      const res = await api.post('/investigations', payload);
      toast.success('Investigación creada exitosamente');
      navigate(`/m/monitoreo-transaccional/investigaciones/${res.data.id}`);
    } catch (error) {
      toast.error('Error al crear la investigación');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getClienteType = (cliente) => {
    if (cliente.length === 11) return { label: 'CUIT', color: 'bg-blue-100 text-blue-700' };
    if (cliente.length === 22) return { label: 'CVU/CBU', color: 'bg-purple-100 text-purple-700' };
    return { label: '?', color: 'bg-slate-100 text-slate-700' };
  };

  // Set default dates
  React.useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setFormData(prev => ({
      ...prev,
      fecha_hasta: today.toISOString().split('T')[0],
      fecha_desde: thirtyDaysAgo.toISOString().split('T')[0]
    }));
  }, []);

  return (
    <div className="p-4 lg:p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/m/monitoreo-transaccional/investigaciones')}
        >
          <ArrowLeft size={16} className="mr-1" />
          Volver
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileSearch className="text-purple-600" />
            Nueva Investigación
          </h1>
          <p className="text-sm text-slate-600">
            Configure los parámetros de análisis
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Título */}
            <Card className="p-6 bg-white">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Información General</h2>
              <div>
                <Label>Título de la Investigación (opcional)</Label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  placeholder="Ej: Análisis de actividad sospechosa - Cliente XYZ"
                  className="mt-1"
                  style={{ backgroundColor: '#fff' }}
                />
                <p className="text-xs text-slate-400 mt-1">
                  Si no especifica un título, se generará automáticamente
                </p>
              </div>
            </Card>

            {/* Clientes */}
            <Card className="p-6 bg-white">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Users size={20} />
                Clientes a Investigar
              </h2>
              
              <div className="mb-4">
                <Label>Agregar Cliente (CUIT o CVU/CBU)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={formData.clienteInput}
                    onChange={(e) => setFormData({ ...formData, clienteInput: e.target.value.replace(/\D/g, '') })}
                    onKeyPress={handleKeyPress}
                    placeholder="Ej: 20123456789 o 0000357300000000034555"
                    className={errors.clientes ? 'border-red-500' : ''}
                    style={{ backgroundColor: '#fff' }}
                    maxLength={22}
                  />
                  <Button
                    type="button"
                    onClick={handleAddCliente}
                    variant="outline"
                  >
                    <Plus size={16} className="mr-1" />
                    Agregar
                  </Button>
                </div>
                {errors.clientes && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.clientes}
                  </p>
                )}
              </div>

              {/* Lista de clientes agregados */}
              {formData.clientes.length > 0 ? (
                <div className="space-y-2">
                  <Label className="text-slate-500">
                    Clientes agregados ({formData.clientes.length})
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {formData.clientes.map((cliente, idx) => {
                      const type = getClienteType(cliente);
                      return (
                        <div
                          key={idx}
                          className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2"
                        >
                          <Badge className={type.color}>{type.label}</Badge>
                          <span className="font-mono text-sm">{cliente}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveCliente(cliente)}
                            className="text-slate-400 hover:text-red-500"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                  <Users size={32} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-400">
                    Agregue al menos un cliente para investigar
                  </p>
                </div>
              )}
            </Card>

            {/* Rango de Fechas */}
            <Card className="p-6 bg-white">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Calendar size={20} />
                Período de Análisis
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha Desde</Label>
                  <Input
                    type="date"
                    value={formData.fecha_desde}
                    onChange={(e) => setFormData({ ...formData, fecha_desde: e.target.value })}
                    className={`mt-1 ${errors.fecha_desde ? 'border-red-500' : ''}`}
                    style={{ backgroundColor: '#fff' }}
                  />
                  {errors.fecha_desde && (
                    <p className="text-sm text-red-500 mt-1">{errors.fecha_desde}</p>
                  )}
                </div>
                <div>
                  <Label>Fecha Hasta</Label>
                  <Input
                    type="date"
                    value={formData.fecha_hasta}
                    onChange={(e) => setFormData({ ...formData, fecha_hasta: e.target.value })}
                    className={`mt-1 ${errors.fecha_hasta ? 'border-red-500' : ''}`}
                    style={{ backgroundColor: '#fff' }}
                  />
                  {errors.fecha_hasta && (
                    <p className="text-sm text-red-500 mt-1">{errors.fecha_hasta}</p>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Columna Lateral */}
          <div className="space-y-6">
            {/* Tipo de Análisis */}
            <Card className="p-6 bg-white">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Tipo de Análisis</h2>
              
              <RadioGroup
                value={formData.tipo_analisis}
                onValueChange={(value) => setFormData({ ...formData, tipo_analisis: value })}
                className="space-y-4"
              >
                <div className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.tipo_analisis === 'individual' 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}>
                  <RadioGroupItem value="individual" id="individual" className="mt-1" />
                  <div>
                    <Label htmlFor="individual" className="font-medium cursor-pointer">
                      Solo Individual
                    </Label>
                    <p className="text-sm text-slate-500 mt-1">
                      Analiza cada cliente por separado. Incluye métricas, evolución temporal, 
                      contrapartes y patrones sospechosos.
                    </p>
                  </div>
                </div>
                
                <div className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.tipo_analisis === 'individual_relacionado' 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}>
                  <RadioGroupItem value="individual_relacionado" id="individual_relacionado" className="mt-1" />
                  <div>
                    <Label htmlFor="individual_relacionado" className="font-medium cursor-pointer">
                      Individual + Relacionado
                    </Label>
                    <p className="text-sm text-slate-500 mt-1">
                      Además del análisis individual, detecta cruces entre los clientes:
                      transacciones directas, contrapartes comunes y patrones de red.
                    </p>
                    {formData.clientes.length < 2 && (
                      <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                        <AlertCircle size={12} />
                        Requiere al menos 2 clientes
                      </p>
                    )}
                  </div>
                </div>
              </RadioGroup>
            </Card>

            {/* Info */}
            <Card className="p-4 bg-blue-50 border-blue-200">
              <div className="flex gap-3">
                <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">¿Qué incluye el análisis?</p>
                  <ul className="space-y-1 text-blue-700">
                    <li>• Perfil y métricas del cliente</li>
                    <li>• Evolución temporal de transacciones</li>
                    <li>• Top contrapartes (entrada/salida)</li>
                    <li>• Detección de patrones sospechosos</li>
                    <li>• Red de vínculos</li>
                    {formData.tipo_analisis === 'individual_relacionado' && (
                      <>
                        <li className="text-purple-700 font-medium">• Transacciones directas entre clientes</li>
                        <li className="text-purple-700 font-medium">• Contrapartes en común</li>
                        <li className="text-purple-700 font-medium">• Grafo de relaciones</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </Card>

            {/* Botón Submit */}
            <Button
              type="submit"
              disabled={loading || formData.clientes.length === 0}
              className="w-full h-12 text-lg"
              style={{ backgroundColor: '#7c3aed' }}
              data-testid="submit-investigation-btn"
            >
              {loading ? (
                <>
                  <RefreshCw size={20} className="mr-2 animate-spin" />
                  Analizando...
                </>
              ) : (
                <>
                  <Search size={20} className="mr-2" />
                  Iniciar Investigación
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default InvestigacionNueva;
